import {
  downloadFile,
  type FuelAnalysisData,
  type FuelConsumptionDetail,
  type FuelConsumptionListData,
  type FuelContextData,
  type FuelMonthStatus,
  type FuelOverviewData,
  type FuelPeriodData,
  type FuelPeriodExport,
  type FuelPeriodImpact,
  type FuelReconciliationData,
  type FuelRecordDetail,
  type FuelRecordSummary,
  type FuelSettingsData,
  type FuelSource,
  type FuelTanksData,
  type FuelType,
} from '@felix/ui';
import { api } from './api.js';

/**
 * Ofiste yakıt yönetimi (ADR 0117 §1): tanklar gemi verisidir, ofis `fuel.view` yetkili olduğu
 * gemilerinkini salt okunur görür (yazma yok, mimari belge §8). Yakıt ana kaydı şirket verisidir,
 * ofiste yazılır ve `fuel_management` bildiren gemilere iner.
 */
const query = (vesselId: string | null) =>
  vesselId ? `?vesselId=${encodeURIComponent(vesselId)}` : '';

export function officeFuelSource(): FuelSource {
  return {
    scope: 'web',
    tanks: (vesselId) => api<FuelTanksData>(`/office/fuel/tanks${query(vesselId)}`),
    // Açılış, alım, hareket ve defter gemi verisidir: ofiste salt okunur (ADR 0117 Ek Y2)
    overview: (vesselId, days) => {
      const params = new URLSearchParams();
      if (vesselId) params.set('vesselId', vesselId);
      if (days) params.set('days', String(days));
      const text = params.toString();
      return api<FuelOverviewData>(`/office/fuel/overview${text ? `?${text}` : ''}`);
    },
    // Tüketim sefer raporunun bölümüdür, gemi verisi: ofiste salt okunur (ADR 0117 Ek Y3)
    consumptions: (vesselId) =>
      api<FuelConsumptionListData>(`/office/fuel/consumption${query(vesselId)}`),
    consumption: (vesselId, reportId) =>
      api<FuelConsumptionDetail>(`/office/fuel/consumption/${reportId}${query(vesselId)}`),
    context: (vesselId) => api<FuelContextData>(`/office/fuel/context${query(vesselId)}`),
    records: (vesselId, kind) =>
      api<{ items: FuelRecordSummary[] }>(`/office/fuel/records/${kind}${query(vesselId)}`),
    record: (vesselId, kind, id) =>
      api<FuelRecordDetail>(`/office/fuel/records/${kind}/${id}${query(vesselId)}`),
    // Y4 (ADR 0117 Ek Y4): ölçüm, talep ve ay kapanışı gemi verisidir; ofis düzeltmenin ofis
    // adımına karar verir ve şirketin mutabakat ayarını yazar
    reconciliation: (vesselId, soundingId) => {
      const params = new URLSearchParams();
      if (vesselId) params.set('vesselId', vesselId);
      if (soundingId) params.set('soundingId', soundingId);
      const text = params.toString();
      return api<FuelReconciliationData>(`/office/fuel/reconciliation${text ? `?${text}` : ''}`);
    },
    month: (vesselId, month) =>
      api<FuelMonthStatus>(`/office/fuel/months/${month}${query(vesselId)}`),
    // Y7 (ADR 0117 Ek Y7): dönem kilidi ofisin kararıdır; dışa aktarım `fuel.export`
    period: (vesselId, month) =>
      api<FuelPeriodData>(`/office/fuel/periods/${month}${query(vesselId)}`),
    periodExport: (vesselId, month) =>
      api<FuelPeriodExport>(`/office/fuel/periods/${month}/export${query(vesselId)}`),
    analysis: (vesselId, filters) => {
      const params = new URLSearchParams();
      if (vesselId) params.set('vesselId', vesselId);
      for (const [key, value] of Object.entries(filters)) {
        if (value !== null && value !== undefined && value !== '') params.set(key, String(value));
      }
      const text = params.toString();
      return api<FuelAnalysisData>(`/office/fuel/analysis${text ? `?${text}` : ''}`);
    },
    settings: () => api<FuelSettingsData>('/office/fuel/settings'),
    officeEditor: {
      decideAdjustment: async (vesselId, id, input) => {
        await api(`/office/fuel/records/adjustment/${id}/decide${query(vesselId)}`, {
          method: 'POST',
          body: input,
        });
      },
      saveSettings: (input) =>
        api<FuelSettingsData>('/office/fuel/settings', { method: 'PUT', body: input }),
      lockPeriod: async (vesselId, month, input) => {
        await api(`/office/fuel/periods/${month}/lock${query(vesselId)}`, {
          method: 'POST',
          body: input,
        });
      },
      periodImpact: (vesselId, month) =>
        api<{ report: FuelPeriodImpact; hash: string }>(
          `/office/fuel/periods/${month}/impact${query(vesselId)}`,
        ),
      reopenPeriod: async (vesselId, month, input) => {
        await api(`/office/fuel/periods/${month}/reopen${query(vesselId)}`, {
          method: 'POST',
          body: input,
        });
      },
    },
    saveFile: async (fileName, bytes, type) => downloadFile(fileName, bytes, type),
    typeEditor: {
      save: ({ id, rowVersion, fuelType }) =>
        id === undefined
          ? api<FuelType>('/office/fuel/types', { method: 'POST', body: { fuelType } })
          : api<FuelType>(`/office/fuel/types/${id}`, {
              method: 'PUT',
              body: { rowVersion, fuelType },
            }),
    },
  };
}
